import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { ArrowBackRounded, DownloadRounded } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { pdf } from '@react-pdf/renderer';
import { openGeneratedPdf } from '../../../lib/open-generated-pdf';
import { renderWithoutHyphenation } from '../../../lib/pdf-hyphenation';
import { db } from '../../../lib/firebase';
import { useEnrolledSections } from '../hooks/use-portal-data';
import { useStudentIdentity } from '../../../hooks/use-student-identity';
import { useAuth } from '../../../contexts/auth.context';
import { usePortalView } from '../../../stores/portal-view.store';
import { useTenantStore } from '../../../stores/tenant.store';
import { PortalLoading } from '../PortalShell';
import { deriveSectionLifecycle, formatDateEs } from '../util/lifecycle';
import { todayInZoneWeb } from '@dosfilos/shared';
import { useTenantTimeZone } from '../../../stores/tenant.store';
import { TranscriptPdfDocument, type TranscriptPdfData } from './TranscriptPdfDocument';
import { useTenantConfig } from '../../../hooks/use-tenant-config';
import { formatGrade } from '../../../lib/grading';
import { formatDate } from '../../../lib/i18n-format';

interface Branding {
  tenantName: string;
  logoUrl?: string;
  primaryColor: string;
}

const CARD_SX = { bgcolor: '#161b22', border: '1px solid #21262d', borderRadius: 2 } as const;

function StatBlock({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="#8b949e">
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} sx={{ color: '#e6edf3' }}>
        {value}
      </Typography>
    </Box>
  );
}

/**
 * Student transcript. On-screen readable summary + a real branded PDF
 * (TranscriptPdfDocument via @react-pdf/renderer) opened in a new tab — no
 * window.print() of the DOM. Marked unofficial (diagonal watermark in the PDF).
 */
export function TranscriptPage(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation('sis');
  const { gradingScale } = useTenantConfig();
  const sectionsQ = useEnrolledSections();
  const { user } = useAuth();
  // «Ver portal»: el nombre sale de la ficha del alumno (abajo), nunca de la
  // sesión, que es la del personal que mira.
  const view = usePortalView();
  const { currentTenantId } = useTenantStore();
  const { personId } = useStudentIdentity();

  const displayName = view
    ? (view.studentCode ?? t('portal.view.thisStudent'))
    : (user?.displayName ?? user?.email ?? t('portal.shell.defaultStudent'));

  // Tenant branding — fetched fresh from Firestore (the store theme is not
  // reliable here). Covers both top-level and config.* shapes.
  const [brand, setBrand] = useState<Branding>({
    tenantName: 'Seminario',
    primaryColor: '#111111',
  });
  useEffect(() => {
    if (!currentTenantId) return;
    let alive = true;
    getDoc(doc(db, `tenants/${currentTenantId}`)).then((snap) => {
      if (!alive || !snap.exists()) return;
      const d = snap.data();
      const cfg = (d['config'] ?? {}) as Record<string, unknown>;
      const logoUrl =
        (d['logoUrl'] as string | undefined) ??
        (cfg['documentLogoUrl'] as string | undefined) ??
        (cfg['logoUrl'] as string | undefined);
      setBrand({
        tenantName: (d['name'] as string | undefined) ?? 'Seminario',
        primaryColor:
          (d['primaryColor'] as string | undefined) ??
          (cfg['primaryColor'] as string | undefined) ??
          '#111111',
        ...(logoUrl ? { logoUrl } : {}),
      });
    });
    return () => {
      alive = false;
    };
  }, [currentTenantId]);

  // Student name + email from the person record (also in a portal view).
  const [student, setStudent] = useState<{ name: string; email: string }>({ name: '', email: '' });
  useEffect(() => {
    if (!currentTenantId || !personId) return;
    let alive = true;
    getDoc(doc(db, `tenants/${currentTenantId}/people/${personId}`)).then((snap) => {
      if (!alive || !snap.exists()) return;
      const d = snap.data();
      const full =
        (d['name'] as string | undefined) ||
        [d['firstName'], d['lastName']].filter(Boolean).join(' ');
      setStudent({ name: full || displayName, email: (d['email'] as string | undefined) ?? '' });
    });
    return () => {
      alive = false;
    };
  }, [currentTenantId, personId, displayName]);

  const studentName = student.name || displayName;
  const studentEmail = student.email || (!view ? (user?.email ?? '') : '');

  const sections = sectionsQ.data?.enrolledSections ?? [];

  const todayYmd = todayInZoneWeb(useTenantTimeZone());
  const rows = useMemo(() => {
    return sections
      .map((s) => ({ s, lc: deriveSectionLifecycle(s, todayYmd) }))
      .filter(({ lc }) => lc === 'concluded' || lc === 'archived')
      .sort((a, b) => {
        const t = (a.s.termName ?? '').localeCompare(b.s.termName ?? '');
        return t !== 0 ? t : (a.s.endDate ?? '').localeCompare(b.s.endDate ?? '');
      })
      .map(({ s }) => ({
        term: s.termName ?? '—',
        code: s.courseCode,
        course: s.courseName,
        faculty: s.facultyName ?? '',
        credits: s.credits ?? 0,
        grade: typeof s.finalGrade === 'number' ? s.finalGrade : null,
        // Como la escribe el acta, en la escala del seminario («5.5», «B+»).
        gradeText: formatGrade({ value: s.finalGrade, label: s.finalGradeLabel }, gradingScale),
        finishedOn: s.endDate ? formatDateEs(s.endDate) : '',
      }));
  }, [sections, todayYmd, gradingScale]);

  const totals = useMemo(() => {
    const totalCredits = rows.reduce((sum, r) => sum + r.credits, 0);
    const graded = rows.filter((r) => typeof r.grade === 'number' && r.credits > 0);
    const wSum = graded.reduce((sum, r) => sum + (r.grade as number) * r.credits, 0);
    const cSum = graded.reduce((sum, r) => sum + r.credits, 0);
    return {
      coursesCompleted: rows.length,
      totalCredits,
      weightedGpa: cSum > 0 ? wSum / cSum : null,
    };
  }, [rows]);

  const generatedOnText = t('transcript.generatedOn', {
    date: formatDate(new Date(), {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }),
  });

  const buildPdfData = (): TranscriptPdfData => ({
    brand,
    student: { name: studentName, email: studentEmail },
    generatedOn: generatedOnText,
    rows,
    totals,
    labels: {
      title: t('transcript.title'),
      watermark: t('transcript.draft.watermark'),
      draftBadge: t('transcript.draft.badge'),
      draftNote: t('transcript.draft.note'),
      studentHeading: t('transcript.student.heading'),
      name: t('transcript.student.name'),
      email: t('transcript.student.email'),
      coursesCompleted: t('transcript.totals.courses'),
      totalCredits: t('transcript.totals.credits'),
      gpa: t('transcript.totals.gpa'),
      colTerm: t('transcript.colTerm'),
      colCode: t('transcript.colCode'),
      colCourse: t('transcript.colCourse'),
      colFaculty: t('transcript.colFaculty'),
      colCredits: t('transcript.colCredits'),
      colGrade: t('transcript.colGrade'),
      colFinished: t('transcript.colFinished'),
      empty: t('transcript.empty'),
      footerDisclaimer: t('transcript.draft.note'),
    },
  });

  // The tab opens inside the click and the certificate is generated into it:
  // opening it after the render was blocked by browsers as an unrequested popup.
  const handleDownload = () =>
    openGeneratedPdf(
      () =>
        renderWithoutHyphenation(() =>
          pdf(<TranscriptPdfDocument data={buildPdfData()} />).toBlob(),
        ),
      t('transcript.title'),
    );

  if (sectionsQ.isLoading) return <PortalLoading />;

  return (
    <Stack spacing={2.5} sx={{ color: '#e6edf3', pb: 6 }}>
      <Button
        startIcon={<ArrowBackRounded />}
        onClick={() => navigate('/portal-v2/historial')}
        sx={{ color: '#8b949e', alignSelf: 'flex-start' }}
      >
        {t('transcript.back')}
      </Button>

      {/* Title + primary action */}
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        useFlexGap
      >
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#e6edf3' }}>
              {t('transcript.title')}
            </Typography>
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={t('transcript.draft.badge')}
            />
          </Stack>
          <Typography variant="body2" sx={{ color: '#8b949e' }}>
            {t('transcript.draft.note')}
          </Typography>
          <Typography variant="caption" sx={{ color: '#6e7681' }}>
            {generatedOnText}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<DownloadRounded />} onClick={handleDownload}>
          {t('transcript.downloadPdf')}
        </Button>
      </Stack>

      {/* Summary */}
      <Card sx={CARD_SX} elevation={0}>
        <CardContent>
          <Typography
            variant="overline"
            sx={{ color: '#8b949e', fontWeight: 700, letterSpacing: 1 }}
          >
            {t('transcript.summary')}
          </Typography>
          <Stack direction="row" spacing={5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            <StatBlock label={t('transcript.totals.courses')} value={totals.coursesCompleted} />
            <StatBlock label={t('transcript.totals.credits')} value={totals.totalCredits} />
            {totals.weightedGpa !== null && (
              <StatBlock label={t('transcript.totals.gpa')} value={totals.weightedGpa.toFixed(2)} />
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Student */}
      <Card sx={CARD_SX} elevation={0}>
        <CardContent>
          <Typography
            variant="overline"
            sx={{ color: '#8b949e', fontWeight: 700, letterSpacing: 1 }}
          >
            {t('transcript.student.heading')}
          </Typography>
          <Stack direction="row" spacing={5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            <StatBlock label={t('transcript.student.name')} value={studentName} />
            {studentEmail && (
              <StatBlock label={t('transcript.student.email')} value={studentEmail} />
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* History */}
      <Card sx={CARD_SX} elevation={0}>
        <CardContent>
          <Typography
            variant="overline"
            sx={{ color: '#8b949e', fontWeight: 700, letterSpacing: 1 }}
          >
            {t('transcript.history')}
          </Typography>
          {rows.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#6e7681', mt: 1 }}>
              {t('transcript.empty')}
            </Typography>
          ) : (
            <Box
              component="table"
              sx={{
                width: '100%',
                mt: 1.5,
                borderCollapse: 'collapse',
                '& th, & td': {
                  borderBottom: '1px solid #21262d',
                  padding: '8px 10px',
                  textAlign: 'left',
                  fontSize: 13,
                },
                '& th': {
                  color: '#8b949e',
                  fontWeight: 700,
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                },
              }}
            >
              <thead>
                <tr>
                  <th>{t('transcript.colTerm')}</th>
                  <th>{t('transcript.colCode')}</th>
                  <th>{t('transcript.colCourse')}</th>
                  <th>{t('transcript.colFaculty')}</th>
                  <th style={{ textAlign: 'right' }}>{t('transcript.colCredits')}</th>
                  <th style={{ textAlign: 'right' }}>{t('transcript.colGrade')}</th>
                  <th>{t('transcript.colFinished')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.term}</td>
                    <td>{r.code}</td>
                    <td>{r.course}</td>
                    <td>{r.faculty || '—'}</td>
                    <td style={{ textAlign: 'right' }}>{r.credits}</td>
                    <td style={{ textAlign: 'right' }}>{r.gradeText}</td>
                    <td>{r.finishedOn || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Box>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
